import React, { useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { FileText, Shield, Award, Wrench, Droplet, Download, ExternalLink } from "lucide-react";
import { resources } from "../data/resources";

const Resources = () => {
  const [activeSection, setActiveSection] = useState("all");

  const handleDownload = (resource) => {
    // In production, this would trigger actual PDF download
    alert(`Document: ${resource.title}\n\nTo access this document, please contact us at:\nPhone: 604-616-3986\nEmail: timberlakebuildingcenter@gmail.com`);
  };

  const ResourceCard = ({ resource }) => (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
      <div className="relative h-48 overflow-hidden">
        <img
          src={resource.thumbnail}
          alt={resource.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
        <div className="absolute top-2 right-2 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
          {resource.fileType}
        </div>
      </div>
      <div className="p-6">
        {resource.collection && (
          <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
            {resource.collection}
          </span>
        )}
        <h3 className="text-lg font-bold text-gray-800 mt-3 mb-2">
          {resource.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          {resource.description}
        </p>
        {resource.warranty && (
          <p className="text-sm text-gray-700 mb-3">
            <span className="font-semibold">Warranty:</span> {resource.warranty}
          </p>
        )}
        <button
          onClick={() => handleDownload(resource)}
          className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Request Document</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">
            Resources Center
          </h1>
          <p className="text-xl text-blue-100">
            Installation Instructions, Warranty Information & Product Documentation
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Quick Navigation */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-12">
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setActiveSection("all")}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === "all"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Resources
            </button>
            <button
              onClick={() => setActiveSection("sell-sheets")}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === "sell-sheets"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Sell Sheets
            </button>
            <button
              onClick={() => setActiveSection("warranties")}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === "warranties"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Warranties
            </button>
            <button
              onClick={() => setActiveSection("certifications")}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === "certifications"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Certifications
            </button>
            <button
              onClick={() => setActiveSection("installation")}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === "installation"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Installation
            </button>
            <button
              onClick={() => setActiveSection("maintenance")}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                activeSection === "maintenance"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Maintenance
            </button>
          </div>
        </div>

        {/* Collection Sell Sheets */}
        {(activeSection === "all" || activeSection === "sell-sheets") && (
          <section id="sell-sheets" className="mb-16">
            <div className="flex items-center mb-6">
              <FileText className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  Collection Sell Sheets
                </h2>
                <p className="text-gray-600">
                  Product specifications and features for each collection
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.sellSheets.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </section>
        )}

        {/* Product Warranties */}
        {(activeSection === "all" || activeSection === "warranties") && (
          <section id="warranties" className="mb-16">
            <div className="flex items-center mb-6">
              <Shield className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  Product Warranties
                </h2>
                <p className="text-gray-600">
                  Limited warranty information for all flooring products
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.warranties.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </section>
        )}

        {/* Certifications */}
        {(activeSection === "all" || activeSection === "certifications") && (
          <section id="certifications" className="mb-16">
            <div className="flex items-center mb-6">
              <Award className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  Certifications
                </h2>
                <p className="text-gray-600">
                  Environmental and quality certifications
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.certifications.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </section>
        )}

        {/* Installation Instructions */}
        {(activeSection === "all" || activeSection === "installation")} && (
          <section id="installation" className="mb-16">
            <div className="flex items-center mb-6">
              <Wrench className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  Installation Instructions
                </h2>
                <p className="text-gray-600">
                  Step-by-step installation guides for each product type
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.installationGuides.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </section>
        )}

        {/* Care & Maintenance Guides */}
        {(activeSection === "all" || activeSection === "maintenance") && (
          <section id="maintenance" className="mb-16">
            <div className="flex items-center mb-6">
              <Droplet className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h2 className="text-3xl font-bold text-gray-800">
                  Care & Maintenance Guides
                </h2>
                <p className="text-gray-600">
                  Keep your floors looking beautiful for years to come
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.maintenanceGuides.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </section>
        )}

        {/* Contact CTA */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Need Additional Support?
          </h2>
          <p className="text-blue-100 mb-6 text-lg">
            Our team is here to help you with product selection, technical questions, and installation support.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/contact"
              className="inline-flex items-center justify-center bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </a>
            <a
              href="/request-quote"
              className="inline-flex items-center justify-center bg-blue-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-900 transition-colors"
            >
              Request Quote
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Resources;
